import { trackEvent } from "@/lib/consent";

// Centralized product-analytics funnel for SundayAtlas. Every event goes
// through the consent-gated trackEvent (no-op unless the user accepted
// analytics) and carries ONLY non-identifying properties: sources, counts,
// booleans, and generic type labels. Never destination names, trip titles,
// emails, or free text. Keeping the taxonomy in one file means event names
// stay consistent across the app and are easy to audit for privacy.
//
// Complements the SERVER-side click logging in /api/click (which records
// affiliate/video opens in Redis for the operator stats page); this layer
// is the client-side funnel for retention/drop-off analysis in Vercel
// Analytics.

// ===== Core funnel =====

export function trackTripStarted(
  source: "landing" | "dashboard" | "direct",
  hasPrompt: boolean
) {
  trackEvent("trip_started", { source, has_prompt: hasPrompt });
}

export function trackTripCreated(source: "landing" | "dashboard" | "direct") {
  trackEvent("trip_created", { source });
}

// Decision cards (present_options): the AI presented a fork as tappable
// cards, and the user answered by tapping. No properties: the question
// and labels are trip content.
export function trackDecisionCardsShown() {
  trackEvent("decision_cards_shown");
}

export function trackDecisionCardChosen() {
  trackEvent("decision_card_chosen");
}

// The structured trip composer on the landing hero. Counts and booleans
// only (no destination, no free text, per the privacy rule above).
export function trackComposerTripStarted(meta: {
  vibeCount: number;
  usedPreset: boolean;
  structured: boolean;
}) {
  trackEvent("composer_trip_started", {
    vibe_count: meta.vibeCount,
    used_preset: meta.usedPreset,
    structured: meta.structured,
  });
}

// The user's FIRST concierge message on a given trip (funnel activation).
export function trackFirstMessageSent() {
  trackEvent("first_message_sent");
}

// The AI produced structure (first day or item landed on an empty trip).
export function trackTripBuilt() {
  trackEvent("trip_built");
}

export function trackTripShared() {
  trackEvent("trip_shared");
}

// ===== Engagement depth =====

// Fired at message-count milestones on a trip (5, 10, 20) to gauge depth
// without emitting an event per message.
export function trackMessageMilestone(count: number) {
  trackEvent("message_milestone", { count });
}

// Opened an existing (non-empty) trip: a returning-usage signal.
export function trackReturningTripOpened() {
  trackEvent("returning_trip_opened");
}

export function trackSignIn() {
  trackEvent("sign_in");
}

// ===== Inspiration feature usage =====

export function trackInspirationOpened() {
  trackEvent("inspiration_opened");
}

export function trackInspirationVideoExpanded() {
  trackEvent("inspiration_video_expanded");
}

export function trackInspirationPasteUsed() {
  trackEvent("inspiration_paste_used");
}

export function trackPlaceAddedFromInspiration() {
  trackEvent("place_added_from_inspiration");
}

// ===== Booking / affiliate intent =====

// Client-side counterpart to the server /api/click log, so revenue-intent
// shows up in the same funnel. `kind` is a generic label, not a URL.
export function trackAffiliateClick(kind: "booking" | "video" | "source") {
  trackEvent("affiliate_click", { kind });
}
